import { ComponentType, SVGProps } from 'react';
import { learnLevelName, learnSessionScoreMax, moduleProgress } from '../../../../common/src/learn_rules.ts';
import { LearnModule, LearnProgress } from '../../../../common/src/model.ts';
import { getLearnPath, LearnPages } from '../../../../common/src/pages.ts';
import HarmonyIcon from '../../assets/learn-harmony.svg?react';
import IntervalIcon from '../../assets/learn-interval.svg?react';
import MicIcon from '../../assets/learn-mic.svg?react';
import PitchIcon from '../../assets/learn-pitch.svg?react';
import RhythmIcon from '../../assets/learn-rhythm.svg?react';
import SolfegeIcon from '../../assets/learn-solfege.svg?react';

// The Learn tab's classes and their modules (issues #3700, #3719), in one place: what each class
// and module is called, its icon, the line on what it does, and - for a module that is built - the
// page it lives on and the progress record it reports under. The class screens render from this,
// and so do the hub's class cards. Building a module is a change here (give it `built`), a
// LearnPages entry and its element in routes.tsx; a module without `built` shows as a locked
// "Coming soon" tile, and a class without a `screen` as a locked "Coming soon" card on the hub.
//
// Everything is open (decided 2026-10-01): no module waits on another, and the order a class
// lists its modules in is only the order we suggest.

export type LearnIcon = ComponentType<SVGProps<SVGSVGElement>>;

export enum LearnClassId {
  Pitch = 'pitch',
  Rhythm = 'rhythm',
  Harmony = 'harmony',
}

export interface BuiltLearnModule {
  page: LearnPages;
  module: LearnModule; // the progress record its sessions are recorded under
  needsRange: boolean; // draws its notes from the measured range, so the Range Finder comes first
}

export interface LearnModuleInfo {
  name: string;
  description: string; // one line on what it does
  icon: LearnIcon;
  built?: BuiltLearnModule; // absent while the module is coming soon
}

// The line under a class screen's title: the singer's part and range (Pitch), or a sentence
export type LearnClassSingerLine = { kind: 'partAndRange' } | { kind: 'text'; text: string };

export interface LearnClassScreenInfo {
  page: LearnPages;
  singerLine: LearnClassSingerLine;
  suggestedOrder: string;
}

export interface LearnClassInfo {
  id: LearnClassId;
  name: string;
  description: string; // the line under the class name on its hub card
  icon: LearnIcon;
  screen?: LearnClassScreenInfo; // absent while the class has no screen of its own (Harmony)
  modules: LearnModuleInfo[]; // in the order we suggest
}

export const learnClasses: LearnClassInfo[] = [
  {
    id: LearnClassId.Pitch,
    name: 'Pitch',
    description: 'Notes, then intervals, then phrases',
    icon: PitchIcon,
    screen: {
      page: LearnPages.Pitch,
      singerLine: { kind: 'partAndRange' },
      suggestedOrder:
        'Three modules. We suggest this order: hold one note, sing short patterns from home, then sing hymn phrases.',
    },
    modules: [
      {
        name: 'Pitch Match',
        description: 'Hear a note, sing it, hold it',
        icon: PitchIcon,
        built: { page: LearnPages.PitchMatch, module: LearnModule.PitchMatch, needsRange: true },
      },
      {
        name: 'Interval Training',
        description: 'Hear a short pattern, sing it back',
        icon: IntervalIcon,
        built: { page: LearnPages.IntervalTraining, module: LearnModule.IntervalTraining, needsRange: true },
      },
      {
        name: 'Sing in Solfège',
        description: 'Sing hymn phrases along with the guide',
        icon: SolfegeIcon,
        built: { page: LearnPages.SingInSolfege, module: LearnModule.SingInSolfege, needsRange: true },
      },
    ],
  },
  {
    id: LearnClassId.Rhythm,
    name: 'Rhythm',
    description: 'Tap it back, then sing it back',
    icon: RhythmIcon,
    screen: {
      page: LearnPages.Rhythm,
      singerLine: { kind: 'text', text: 'Hear a rhythm, then give it back.' },
      suggestedOrder: 'Two modules. We suggest this order: tap a rhythm back, then sing it back.',
    },
    modules: [
      {
        name: 'Rhythm Echo',
        description: 'Hear it, then tap it back',
        icon: RhythmIcon,
        built: { page: LearnPages.RhythmEcho, module: LearnModule.RhythmEcho, needsRange: false },
      },
      {
        name: 'Sing Rhythm',
        description: 'Hear it, then sing it back on one note',
        icon: MicIcon,
        // any note will do, so it needs no measured range
        built: { page: LearnPages.SingRhythm, module: LearnModule.SingRhythm, needsRange: false },
      },
    ],
  },
  {
    id: LearnClassId.Harmony,
    name: 'Harmony',
    description: 'Hold your part with the other three',
    icon: HarmonyIcon,
    modules: [],
  },
];

export const learnClassFootnote = 'Start anywhere. The order above is the one we suggest.';

export function learnClass(id: LearnClassId): LearnClassInfo {
  const found = learnClasses.find((learnClass) => learnClass.id === id);
  if (!found) {
    throw new Error(`No Learn class ${id}`);
  }
  return found;
}

// What a way back to a class screen says ("Back to Pitch")
export function learnClassBackLabel(info: LearnClassInfo): string {
  return `Back to ${info.name}`;
}

// What a way on to a class screen says ("Continue to Pitch")
export function learnClassContinueLabel(info: LearnClassInfo): string {
  return `Continue to ${info.name}`;
}

// What a way into a module says ("Start Pitch Match")
export function learnModuleStartLabel(info: LearnModuleInfo): string {
  return `Start ${info.name}`;
}

// Where an exercise's way out leads, and what its button says: the screen of the class the module
// belongs to ("Back to Pitch"). Every LearnModule is built in a class that has a screen (a test
// holds the list above to that). How it gets there - a step back or a replaced entry - is
// learn_navigation.tsx's.
export function learnModuleBack(module: LearnModule): { label: string; path: string } {
  const owner = learnClasses.find((learnClass) => learnClass.modules.some((info) => info.built?.module === module));
  if (!owner?.screen) {
    throw new Error(`Learn module ${module} belongs to no class screen`);
  }
  return { label: learnClassBackLabel(owner), path: getLearnPath(owner.screen.page) };
}

export type LearnModuleTileKind = 'comingSoon' | 'needsRange' | 'notStarted' | 'started';

// What a module's tile says under its description and where a tap on it goes (nowhere while the
// module is coming soon). A module that needs a range leads into the Range Finder until one is
// saved, on the module's behalf (`next`, so the Range Finder's result leads on into it,
// learn_navigation.tsx); its own page sends a direct link there too.
export interface LearnModuleTileStatus {
  kind: LearnModuleTileKind;
  text: string; // "Medium · Best 9/10 · Continue"
  to?: string;
  next?: string; // with `to` the Range Finder: the module's own path, where its result leads on to
}

export const comingSoonText = 'Coming soon';
export const findRangeFirstText = 'Find your range first · Start';
export const notStartedText = 'Not started · Start';

export function learnModuleTileStatus(progress: LearnProgress, info: LearnModuleInfo): LearnModuleTileStatus {
  const { built } = info;
  if (!built) {
    return { kind: 'comingSoon', text: comingSoonText };
  }
  if (built.needsRange && !progress.range) {
    return {
      kind: 'needsRange',
      text: findRangeFirstText,
      to: getLearnPath(LearnPages.Range),
      next: getLearnPath(built.page),
    };
  }
  const to = getLearnPath(built.page);
  const { level, bestScore, sessions } = moduleProgress(progress, built.module);
  if (sessions === 0) {
    return { kind: 'notStarted', text: notStartedText, to };
  }
  // A level just unlocked has no best yet: its name and Continue, without a score to show
  const best = bestScore[level];
  const score = best === undefined ? '' : ` · Best ${best}/${learnSessionScoreMax}`;
  return { kind: 'started', text: `${learnLevelName(level)}${score} · Continue`, to };
}

type BuiltLearnModuleInfo = LearnModuleInfo & { built: BuiltLearnModule };

// The module a class's hub card reports on: the class's first built module, always. Pitch has three
// built modules now (Pitch Match, Interval Training and Sing in Solfège, issues #3721 and #3722),
// and the card still reports on Pitch Match, however much of the others the singer has played,
// because saved progress does not record which module was played last. Reporting the module last played would need such a
// record in the saved progress, and this is the one place that choice would be made.
export function learnClassActiveModule(info: LearnClassInfo): BuiltLearnModuleInfo | undefined {
  return info.modules.find((module): module is BuiltLearnModuleInfo => module.built !== undefined);
}

// What a class's hub card says under its description and where a tap on it goes: the class
// screen, the Range Finder while the class's module needs a range and none is saved (nothing under
// Pitch is usable without one), or nowhere while the class has no screen (Harmony). The status
// names the module the singer is in and its level ("Pitch Match · Medium · Continue"); it shows no
// best score.
export interface LearnClassCardStatus {
  kind: LearnModuleTileKind;
  text: string;
  to?: string;
}

export function learnClassCardStatus(progress: LearnProgress, info: LearnClassInfo): LearnClassCardStatus {
  if (!info.screen) {
    return { kind: 'comingSoon', text: comingSoonText };
  }
  const to = getLearnPath(info.screen.page);
  const active = learnClassActiveModule(info);
  if (!active) {
    return { kind: 'comingSoon', text: comingSoonText, to };
  }
  // straight into the Range Finder: the class screen would only send the singer there too. The class
  // screen itself still leads there from its tile and its call to action when reached by URL.
  if (active.built.needsRange && !progress.range) {
    return { kind: 'needsRange', text: findRangeFirstText, to: getLearnPath(LearnPages.Range) };
  }
  const { level, sessions } = moduleProgress(progress, active.built.module);
  if (sessions === 0) {
    return { kind: 'notStarted', text: `${active.name} · Start`, to };
  }
  return { kind: 'started', text: `${active.name} · ${learnLevelName(level)} · Continue`, to };
}
